import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsCheck2Square, BsChevronDown, BsExclamation } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Box,
  Button,
  Flex, HStack,
  Icon, IconButton,
  Input,
  Popover,
  PopoverBody,
  PopoverContent, PopoverHeader,
  PopoverTrigger, Text, useDisclosure
} from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';
import { removeArrayItemAtIndex } from '../../utils/arrayUtil';
import LoadingSpinner from '../Spinner';

import { comboBoxStyles, hoverStyle, menuBodyStyles, menuButtonStyles } from './styles';
import { validateCustomValue } from './util';

import './styles.scss';

const MultiSelectDropdown = ({
  placeholder = '',
  data = [],
  idKey = 'id',
  labelKey,
  onChange = () => {},
  values = [],
  isLazy = false,
  dataFetchFunction = () => {},
  allowCustomValues = false,
  customValuePlaceholder = 'Add custom value',
  customValueValidation = '',
  selectAllOption = false,
  selectAllChange = () => {},
  displayLength = 3,
  endMessage = '',
  fetchNextData,
  hasMore = false,
  searchComponent,
  showLabel = true,
  isDisabled = false,
  onClose = () => {},
  popoverId = undefined
}) => {
  const initialItems = useMemo(() => data.map(item => ({
    value: item[idKey],
    label: typeof labelKey === 'function' ? labelKey(item) : item[labelKey]
  })), [data, idKey, labelKey]);

  const normalizedValues = useMemo(() => values.map(val => ({
    value: val[idKey],
    label: typeof labelKey === 'function' ? labelKey(val) : val[labelKey]
  })), [idKey, labelKey, values]);

  const { isOpen, onOpen, onClose: onPopoverClose } = useDisclosure();
  const [isLoading, toggleIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [items, setItems] = useState([]);
  const [selectAllBool, setSelectAllBool] = useState(false);
  const [customInputError, setCustomInputError] = useState();
  const [stagedInputValue, setStagedValue] = useState('');
  const initRef = useRef();
  const contentRef = useRef();

  useEffect(() => {
    async function fetch() {
      if (isLazy && !dataFetched && isOpen) {
        setDataFetched(true);
        toggleIsLoading(true);
        await dataFetchFunction();
        toggleIsLoading(false);
      }
    }

    fetch();
  }, [isLazy, dataFetchFunction, data, isOpen, isLoading, dataFetched]);

  useEffect(() => {
    setItems(initialItems);
  }, [data, initialItems, normalizedValues]); //eslint-disable-line

  const {
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    getLabelProps,
    openMenu,
    closeMenu,
    setInputValue: setCustomOptionValue,
    inputValue: customOptionValue
  } = useCombobox({
    items,
    isOpen,
    onInputValueChange: ({ inputValue }) => {
      setCustomOptionValue(inputValue);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      const currentIndex = normalizedValues.findIndex(item => item.value === selectedItem.value);
      const item = data.find(i => i[idKey] === selectedItem.value);

      if (currentIndex >= 0) {
        onChange(removeArrayItemAtIndex(values, currentIndex));
        if (selectAllBool) {
          setSelectAllBool(false);
        }
      } else {
        onChange([...values, item]);
        if (normalizedValues.length + 1 === items.length) {
          setSelectAllBool(true);
        }
      }
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
          return {
            ...changes,
            highlightedIndex: state.index
          };
        case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
          return {
            ...changes,
            highlightedIndex: state.index
          };
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: '' // don't add the item string as input value at selection.
          };
        default:
          return changes;
      }
    }
  });

  const handleClose = () => {
    onPopoverClose();
    onClose();
    closeMenu();
  };

  const handleOpen = () => {
    openMenu();
    onOpen();
  };

  const selectItemPlaceholder = useMemo(() => {
    if (normalizedValues.length) {
      const value = normalizedValues.slice(1, 3).reduce((display, item) => `${display}, ${item.label}`, normalizedValues[0]?.label);
      return value.length >= 65 ? `${value.substring(0, 65)}...` : value;
    }

    return '';
  }, [normalizedValues]);

  const SearchComponent = searchComponent;
  const renderSearchComponent = () => (
    <PopoverHeader as={Flex} justifyContent="flex-end">
      <SearchComponent />
    </PopoverHeader>
  );

  const onCustomValueInputChange = () => {
    if (!customOptionValue) return;

    const { valid, message } = validateCustomValue(customOptionValue, customValueValidation);
    if (!valid && message) {
      setCustomInputError(message);
    } else {
      const newValue = {
        value: customOptionValue,
        label: customOptionValue,
        type: 'custom'
      };

      setItems([newValue, ...items]);

      onChange([{
        [idKey]: newValue.value,
        [labelKey]: newValue.label,
        type: 'custom'
      }, ...values]);

      setCustomOptionValue('');
      setCustomInputError('');
      setStagedValue('');
    }
  };

  return (
    <Box
      className="MultiSelectDropdownFilters"
      display="inline-flex"
      flexWrap="wrap"
      position="relative"
      width="100%"
    >
      <Popover
        id={popoverId}
        isOpen={isOpen}
        placement="bottom"
        variant="expand"
        onClose={handleClose}
        onOpen={handleOpen}
      >
        <PopoverTrigger>
          <Box
            position="relative"
            style={comboBoxStyles}
            {...getComboboxProps()}
          >
            <Text color="greyScale.500" pb={2} textStyle="bodySm500">{showLabel && placeholder}</Text>
            <Button
              _active={{ color: 'primaryScale.600', borderColor: 'primaryScale.600', borderWidth: '1px' }}
              _disabled={{ opacity: 0.5 }}
              border={isOpen && `1px solid ${colors.primaryScale['600']}`}
              color={isOpen && colors.primaryScale['600']}
              disabled={isDisabled}
              style={menuButtonStyles}
              {...getToggleButtonProps()}
              rightIcon={(
                <Icon
                  as={BsChevronDown}
                  color={isOpen ? colors.primaryScale['600']
                    : colors.greyScale['500']}
                />
            )}
              variant="unstyled"
              width="100%"
            >
              <Box display={normalizedValues.length ? 'none' : 'block'}>
                <Text color={colors.greyScale['500']} textStyle="bodyMd">
                  {placeholder}
                </Text>
              </Box>
              {isLoading
                ? <LoadingSpinner isInline />
                : (
                  normalizedValues.length > displayLength
                    ? (
                      <HStack color={colors.greyScale['900']}>
                        <Box as='span' maxW='200px' overflow='hidden' textOverflow='ellipsis'>{normalizedValues[0].label}</Box>
                        <i className="fas fa-ellipsis-h" />
                        <span>{`+${normalizedValues.length - 1}`}</span>
                      </HStack>
                    )
                    : selectItemPlaceholder
                )}
            </Button>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          _focus={{ outline: 'none', boxShadow: 'none' }}
          borderRadius="md"
          p={0}
          ref={contentRef}
        >
          {searchComponent && renderSearchComponent()}
          <PopoverBody
            style={menuBodyStyles}
            {...getMenuProps({ ref: initRef })}
            textStyle="bodyMd500"
          >
            <ul>
              <InfiniteScroll
                dataLength={data.length}
                endMessage={endMessage}
                hasMore={hasMore}
                height={items.length <= 6 ? 'auto' : 295}
                loader={(
                  <Text color="greyScale.500" pt={2} textAlign="center" textStyle="bodySm">
                    Loading...
                  </Text>
                  )}
                next={fetchNextData}
              >
                <Flex
                  _hover={hoverStyle}
                  alignItems="center"
                  borderRadius="md"
                  display={(selectAllOption && !hasMore && data.length) ? 'flex' : 'none'}
                  height="40px"
                  p={2}
                >
                  <label>
                    <input
                      checked={selectAllBool}
                      type="checkbox"
                      value="checkAll"
                      onChange={() => {
                        selectAllChange(!selectAllBool);
                        setSelectAllBool(!selectAllBool);
                      }}
                    />
                    <span className="checkbox-control" />
                  </label>
                  <Text ml={5} {...getLabelProps()} textTransform="capitalize">
                    {selectAllBool ? 'Deselect All' : 'Select All'}
                  </Text>
                </Flex>
                <Box>
                  <Flex
                    alignItems="center"
                    display={allowCustomValues ? 'flex' : 'none'}
                    height="40px"
                    mb={2}
                    p="8px"
                  >
                    <IconButton
                      aria-label="Add custom recipient button"
                      icon={<Icon as={BsCheck2Square} />}
                      isDisabled={!customOptionValue}
                      size="sm"
                      onClick={onCustomValueInputChange}
                    >
                      Add
                    </IconButton>
                    <Input
                      ml="8px"
                      name="customInput"
                      pl="2px"
                      placeholder={customValuePlaceholder}
                      textStyle="bodyMd"
                      value={stagedInputValue}
                      variant="unstyled"
                      width="100%"
                      {...getInputProps({
                        onKeyDown: e => {
                          if (e.keyCode === 13) {
                            onCustomValueInputChange();
                          }
                        },
                        onChange: ({ target: { value } }) => setStagedValue(value),
                        value: stagedInputValue
                      })}
                    />
                    {customInputError && (
                    <Icon as={BsExclamation} boxSize="18px" color="error.primary.600" />
                    )}
                  </Flex>
                  {customInputError && (
                  <Text color="error.primary.600" pl="2.15rem" textStyle="bodyXs500">{customInputError}</Text>
                  )}
                </Box>
                { !items.length
                  ? (
                    <Box justifyContent="center" p={4}>
                      <Text>None</Text>
                    </Box>
                  )
                  : items.map((item, index) => (
                    <Button
                      _focus={{ ...hoverStyle, outline: 'none' }}
                      _hover={hoverStyle}
                      _selected={{ ...hoverStyle, outline: 'none' }}
                      alignItems="center"
                      borderRadius="md"
                      display="flex"
                      height="40px"
                      justifyContent="flex-start"
                      key={item.value}
                      p={2}
                      variant="unstyled"
                   
                      {...getItemProps({ item, index, key: item.value })}
                    >
                      <label h="16px" {...getLabelProps()} margin-bottom={0} w="16px">
                        <input
                          checked={normalizedValues.findIndex(value => value.value === item.value) >= 0}
                          type="checkbox"
                          value={item.value}
                          onChange={() => {}}
                        />
                        <span className="checkbox-control" />
                      </label>
                      <Box
                        ml={5}
                        textTransform="capitalize"
                        maxW="300px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {item.label}
                      </Box>
                    </Button>
                  ))}
              </InfiniteScroll>
            </ul>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

MultiSelectDropdown.propTypes = {
  /** True if the user can input and select their own value */
  allowCustomValues: PropTypes.bool,
  /** Placeholder text description of custom value */
  customValuePlaceholder: PropTypes.string,
  /** Type of validation for the custom input, one of the `regexPatterns` in regexPatterns.js */
  customValueValidation: PropTypes.string,
  /** Placeholder/title that will appear on or above button */
  placeholder: PropTypes.string,
  /** Data to be displayed in dropdown menu */
  data: PropTypes.arrayOf(PropTypes.object),
  /** Key for the text to be displayed next to checkbox */
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Key for the value of the input */
  idKey: PropTypes.string,
  /** Function for checking/unchecking input */
  onChange: PropTypes.func,
  /** Function to close the dropdown and perform side effects */
  onClose: PropTypes.func,
  /** Lazy loading */
  isLazy: PropTypes.bool,
  /** Function to fetch additional data if not elasticsearch */
  dataFetchFunction: PropTypes.func,
  /** Checked/selected data */
  values: PropTypes.arrayOf(
    PropTypes
      .shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string
      })
  ),
  /** If true, user will be able to select all items in list. `selectAllChange` is required if this is true */
  selectAllOption: PropTypes.bool,
  /** Function for selecting all items from list */
  selectAllChange: PropTypes.func,
  /** How many selected items to display on a button before ellipses. Defaults to 3 */
  displayLength: PropTypes.number,
  /** Message informing user of end of a list. Helpful with larger amounts of data */
  endMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  /** `next` callback for InfiniteScroll to fetch more data */
  fetchNextData: PropTypes.func,
  /** If true, InfiniteScroll will show the loading message and fetch additional data */
  hasMore: PropTypes.bool,
  /** Pass a search component if list should be filterable. For elasticsearch, use SearchGlobalSearchInput */
  searchComponent: PropTypes.func,
  /** True if the form label and the place holder are the same. Defaults to true */
  showLabel: PropTypes.bool,
  /** Flag to disable the dropdown */
  isDisabled: PropTypes.bool,
  /** Id to target the popover trigger div */
  popoverId: PropTypes.string
};

export default MultiSelectDropdown;
